/**
 * Getting your own cards out of the way.
 *
 * A phone screen is mostly hand. That is right while you are deciding and
 * wrong the rest of the time — somebody else is thinking, the trick is being
 * played out, and what you want to look at is the table your cards are sitting
 * on top of.
 *
 * So the hand retreats: off the bottom of the screen apart from a handle, and
 * back up when it is your turn. The automatic part is a **preference**, because
 * a hand that moves on its own is either exactly what you wanted or deeply
 * annoying, and which one depends on the person.
 *
 * It defaults OFF. Moving on its own turned out to be mostly the annoying one:
 * the hand slides away while somebody else is thinking, which is exactly when
 * you want to look at it. The manual handle keeps the screen space available
 * for anyone who wants it, and the preference lives in the Table menu.
 *
 * A manual tap always wins until the next turn boundary. Anything else means
 * the screen argues with you.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const AUTO_KEY = 'cards.autoRetreat';

/**
 * Per-device, and never worth failing over: private browsing throws on both.
 *
 * Off unless somebody asked for it, which is the opposite of how this started.
 * A hand that slides away on its own takes your cards off screen at the exact
 * moment you are planning the next play - somebody else is thinking, which is
 * when you do your thinking too. The handle is still there to drop the hand
 * deliberately, and on a short screen that is worth about 130px of table.
 *
 * `=== 'on'` rather than `!== 'off'`: an unset key now means off, so the
 * default follows from the test rather than from a separate branch.
 */
function readAuto(): boolean {
  try {
    return window.localStorage.getItem(AUTO_KEY) === 'on';
  } catch {
    return false;
  }
}

function writeAuto(value: boolean): void {
  try {
    window.localStorage.setItem(AUTO_KEY, value ? 'on' : 'off');
  } catch {
    /* private browsing */
  }
}

export interface Retreat {
  /** True when the hand is tucked off the bottom of the screen. */
  retreated: boolean;
  /** Whether the hand moves on its own at turn boundaries. */
  auto: boolean;
  toggle: () => void;
  setAuto: (value: boolean) => void;
}

export function useRetreat(myTurn: boolean, active: boolean): Retreat {
  const [auto, setAutoState] = useState(readAuto);
  const [retreated, setRetreated] = useState(false);

  /*
   * Fires on the CHANGE, not on the state.
   *
   * Reacting to `myTurn` being false every render would drag the hand back
   * down the instant you pulled it up, which is the screen arguing with you.
   * Only the transition moves it.
   */
  const wasMyTurn = useRef(myTurn);
  useEffect(() => {
    if (myTurn !== wasMyTurn.current) {
      wasMyTurn.current = myTurn;
      if (auto) setRetreated(!myTurn);
    }
  }, [myTurn, auto]);

  /*
   * Outside a live round there is nothing to decide, so the hand stays put
   * rather than retreating over a lobby or a scoreboard that has the screen to
   * itself already.
   */
  useEffect(() => {
    if (!active) setRetreated(false);
  }, [active]);

  const toggle = useCallback(() => {
    setRetreated((value) => !value);
  }, []);

  const setAuto = useCallback((value: boolean) => {
    setAutoState(value);
    writeAuto(value);
    // Turning it off should leave the hand where it is; turning it on should
    // not yank it either. The next turn boundary does the work.
  }, []);

  return { retreated, auto, toggle, setAuto };
}
